import { useCallback, useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApp } from '@/hooks/use-app-context';
import {
  collectionPlaceErrorKey,
  isConsentGranted,
  needsConsentAnswer,
  type CollectionPlace,
  type CollectionPlaceErrorKey,
  type Place,
} from './collection-place.contract';
import { collectionPlaceQueryOptions, collectionPlaceService } from './collection-place.service';
import { clearDeclaredPlace, readDeclaredPlace, saveDeclaredPlace } from './place-session-store';

export type PlaceStatus = 'asking' | 'locating' | 'ready' | 'manual';
export type PlaceNotice = CollectionPlaceErrorKey | 'positionDenied' | 'consentSaveError';

/**
 * Spec 004 — the place of one collection session. With consent the browser
 * position fills it in; without, the user picks the city. Consent is asked once
 * and stored server-side, so every device restores it from the user.
 */
export function useCollectionPlace() {
  const { user, setUser, language } = useApp();
  const queryClient = useQueryClient();
  const lang = language === 'en' ? 'en' : 'fr';
  const consent = user?.geolocationConsent;
  const [place, setPlace] = useState<CollectionPlace | null>(() => {
    const saved = user?.id ? readDeclaredPlace(user.id) : undefined;
    return saved ? { ...saved, source: 'declared' } : null;
  });
  const [status, setStatus] = useState<PlaceStatus>(() =>
    place ? 'ready' : needsConsentAnswer(consent) ? 'asking' : isConsentGranted(consent) ? 'locating' : 'manual',
  );
  const [notice, setNotice] = useState<PlaceNotice | null>(null);
  const autoLocated = useRef(false);
  const userId = user?.id;

  useEffect(() => {
    autoLocated.current = false;
    const saved = userId ? readDeclaredPlace(userId) : undefined;
    setPlace(saved ? { ...saved, source: 'declared' } : null);
    setStatus(saved ? 'ready' : needsConsentAnswer(consent) ? 'asking' : isConsentGranted(consent) ? 'locating' : 'manual');
  }, [consent, userId]);

  const locate = useCallback(async () => {
    setStatus('locating');
    setNotice(null);
    try {
      const found = await queryClient.fetchQuery(
        collectionPlaceQueryOptions(user?.id ?? 'anonymous', lang),
      );
      setPlace({ ...found, source: 'geolocated' });
      setStatus('ready');
    } catch (error) {
      setNotice(isAxiosError(error) ? collectionPlaceErrorKey(error.response?.status) : 'positionDenied');
      setStatus('manual');
    }
  }, [lang, queryClient, user?.id]);

  useEffect(() => {
    if (autoLocated.current || !isConsentGranted(consent) || place?.source === 'declared') return;
    autoLocated.current = true;
    void locate();
  }, [consent, locate, place?.source]);

  const answer = useMutation({
    mutationFn: collectionPlaceService.recordConsent,
    onSuccess: (state) => {
      if (user) {
        setUser({
          ...user,
          geolocationConsent: {
            decision: state.geolocationConsent,
            version: state.geolocationConsentVersion,
            at: state.geolocationConsentAt,
          },
        });
      }
      if (state.geolocationConsent === 'denied') {
        if (userId) clearDeclaredPlace(userId);
        setPlace(null);
        setStatus('manual');
      }
    },
    onError: () => {
      setNotice('consentSaveError');
      setStatus('manual');
    },
  });

  return {
    place,
    status,
    notice,
    canUsePosition: isConsentGranted(consent),
    isSavingConsent: answer.isPending,
    answerConsent: (decision: 'granted' | 'denied') => answer.mutate(decision),
    choose: (picked: Place) => {
      setPlace({ ...picked, source: 'declared' });
      if (userId) saveDeclaredPlace(userId, picked);
      setNotice(null);
      setStatus('ready');
    },
    change: () => setStatus('manual'),
    locate: () => void locate(),
  };
}

export type CollectionPlaceState = ReturnType<typeof useCollectionPlace>;
